import type { VehicleSide } from "@/lib/api/types"
import { isVinValid } from "@/lib/vin/vin-validator"
import type { DamageZone } from "../types"
import type { CaptureState, Corner, PlannedShot, ShotId } from "./types"

// The damage-led walk, derived from where the consumer said it's hit.
//
//   1. Start at the corner with the most damage (tie → front, then driver).
//   2. For each corner in walking order: the whole car from that corner;
//      if it's damaged, step closer (the damaged panels fill the frame), then
//      one close-up per damaged panel.
//   3. Walk the short way round: across the width first, then down the long
//      side, then across again (2 × width + 1 × length).
//   0. First, when we don't know the car yet: its VIN (scan the door sticker's
//      barcode, photograph it, or type it; skippable). Knowing the car picks
//      the camera's outline (sedan, SUV…) and names the car on every step.
//
// One damaged panel → 4 corners + closer + close-up + sticker = 7 photos.
// Pure; unit-tested in shot-plan.test.ts.

export const CORNERS: readonly Corner[] = ["FL", "FR", "RR", "RL"]

export const CORNER_SIDE: Record<Corner, VehicleSide> = {
  FL: "FRONT_LEFT_CORNER",
  FR: "FRONT_RIGHT_CORNER",
  RR: "REAR_RIGHT_CORNER",
  RL: "REAR_LEFT_CORNER",
}

// What a close-up of each panel is filed under.
export const ZONE_SIDE: Record<DamageZone, VehicleSide> = {
  hood: "FRONT_CENTER",
  "front-bumper": "FRONT_CENTER",
  windshield: "FRONT_CENTER",
  "front-fender-left": "FRONT_LEFT_SIDE",
  "front-fender-right": "FRONT_RIGHT_SIDE",
  "front-door-left": "LEFT_SIDE",
  "front-door-right": "RIGHT_SIDE",
  "rear-door-left": "LEFT_SIDE",
  "rear-door-right": "RIGHT_SIDE",
  "quarter-panel-left": "REAR_LEFT_SIDE",
  "quarter-panel-right": "REAR_RIGHT_SIDE",
  trunk: "REAR_CENTER",
  "rear-bumper": "REAR_CENTER",
  roof: "ROOF",
}

// Display / walking order of panels within one corner (front to back).
export const ZONE_ORDER: readonly DamageZone[] = [
  "front-bumper",
  "hood",
  "windshield",
  "front-fender-left",
  "front-fender-right",
  "front-door-left",
  "front-door-right",
  "rear-door-left",
  "rear-door-right",
  "quarter-panel-left",
  "quarter-panel-right",
  "trunk",
  "rear-bumper",
  "roof",
]

type Axis = "left" | "right" | "center"

const ZONE_AXIS: Record<DamageZone, { end: "front" | "rear" | "middle"; axis: Axis }> = {
  "front-bumper": { end: "front", axis: "center" },
  hood: { end: "front", axis: "center" },
  windshield: { end: "front", axis: "center" },
  "front-fender-left": { end: "front", axis: "left" },
  "front-fender-right": { end: "front", axis: "right" },
  "front-door-left": { end: "front", axis: "left" },
  "front-door-right": { end: "front", axis: "right" },
  "rear-door-left": { end: "rear", axis: "left" },
  "rear-door-right": { end: "rear", axis: "right" },
  "quarter-panel-left": { end: "rear", axis: "left" },
  "quarter-panel-right": { end: "rear", axis: "right" },
  trunk: { end: "rear", axis: "center" },
  "rear-bumper": { end: "rear", axis: "center" },
  roof: { end: "middle", axis: "center" },
}

function cornerOf(end: "front" | "rear", side: "left" | "right"): Corner {
  if (end === "front") return side === "left" ? "FL" : "FR"
  return side === "left" ? "RL" : "RR"
}

// Which corner each damaged panel is photographed from. Centre panels (hood,
// bumpers, trunk) join the side that's also damaged, so the walk stays one
// loop; the roof joins the start corner.
export function assignCorners(zones: readonly DamageZone[]): Map<Corner, DamageZone[]> {
  const unique = ZONE_ORDER.filter((z) => zones.includes(z))
  const hasLeft = unique.some((z) => ZONE_AXIS[z].axis === "left")
  const hasRight = unique.some((z) => ZONE_AXIS[z].axis === "right")
  const centreSide: "left" | "right" = !hasLeft && hasRight ? "right" : "left"

  const byCorner = new Map<Corner, DamageZone[]>()
  const add = (corner: Corner, zone: DamageZone) =>
    byCorner.set(corner, [...(byCorner.get(corner) ?? []), zone])

  for (const zone of unique) {
    const { end, axis } = ZONE_AXIS[zone]
    if (end === "middle") continue // roof — placed once the start corner is known
    add(cornerOf(end, axis === "center" ? centreSide : axis), zone)
  }
  if (unique.includes("roof")) add(startCorner(byCorner), "roof")
  return byCorner
}

// Most damaged corner; ties go to the front, then the driver side.
export function startCorner(byCorner: Map<Corner, DamageZone[]>): Corner {
  const preference: Corner[] = ["FL", "FR", "RL", "RR"]
  let best: Corner = "FL"
  let bestCount = 0
  for (const corner of preference) {
    const count = byCorner.get(corner)?.length ?? 0
    if (count > bestCount) {
      best = corner
      bestCount = count
    }
  }
  return best
}

const ACROSS_WIDTH: Record<Corner, Corner> = { FL: "FR", FR: "FL", RL: "RR", RR: "RL" }
const DOWN_LENGTH: Record<Corner, Corner> = { FL: "RL", RL: "FL", FR: "RR", RR: "FR" }

// Across the width, down the long side, across again.
export function walkOrder(start: Corner): Corner[] {
  const second = ACROSS_WIDTH[start]
  const third = DOWN_LENGTH[second]
  return [start, second, third, ACROSS_WIDTH[third]]
}

export interface ShotPlanInput {
  zones: readonly DamageZone[]
  unsure: boolean
  // Ask for the door sticker (shop wants the vehicle and we don't know it yet).
  includeSticker: boolean
}

export function buildShotPlan({ zones, unsure, includeSticker }: ShotPlanInput): PlannedShot[] {
  const byCorner = unsure ? new Map<Corner, DamageZone[]>() : assignCorners(zones)
  const plan: PlannedShot[] = []

  if (includeSticker) {
    plan.push({
      id: "sticker",
      kind: "sticker",
      corner: null,
      zone: null,
      side: null,
      optional: true,
      autoSnap: false,
    })
  }

  for (const corner of walkOrder(startCorner(byCorner))) {
    plan.push({
      id: `wide:${corner}`,
      kind: "wide",
      corner,
      zone: null,
      side: CORNER_SIDE[corner],
      optional: false,
      autoSnap: true,
    })
    const damaged = byCorner.get(corner) ?? []
    if (damaged.length === 0) continue
    plan.push({
      id: `closer:${corner}`,
      kind: "closer",
      corner,
      zone: null,
      side: closerSide(corner, damaged),
      optional: false,
      autoSnap: false,
    })
    for (const zone of damaged) {
      plan.push({
        id: `closeup:${zone}`,
        kind: "closeup",
        corner,
        zone,
        side: ZONE_SIDE[zone],
        optional: false,
        autoSnap: false,
      })
    }
  }

  if (unsure || zones.length === 0) {
    plan.push({
      id: "closeup:any",
      kind: "closeup",
      corner: null,
      zone: null,
      side: null,
      optional: true,
      autoSnap: false,
    })
  }

  return plan
}

// A closer shot of panels that all sit on one face is filed under that face;
// a mix (fender + bumper) under the corner.
function closerSide(corner: Corner, zones: DamageZone[]): VehicleSide {
  const sides = new Set(zones.filter((z) => z !== "roof").map((z) => ZONE_SIDE[z]))
  return sides.size === 1 ? [...sides][0] : CORNER_SIDE[corner]
}

/* ── Progress helpers ─────────────────────────────────────────────────── */

export function isCaptured(capture: CaptureState, id: ShotId): boolean {
  return !!capture.shots[id]
}

// First planned shot that's neither taken nor skipped.
export function nextOpenShot(
  plan: readonly PlannedShot[],
  capture: CaptureState,
): PlannedShot | null {
  return (
    plan.find((s) => !capture.shots[s.id] && !capture.skipped.includes(s.id)) ?? null
  )
}

export function missingRequired(
  plan: readonly PlannedShot[],
  capture: CaptureState,
): PlannedShot[] {
  return plan.filter((s) => !s.optional && !capture.shots[s.id])
}

export function capturedCount(plan: readonly PlannedShot[], capture: CaptureState): number {
  return plan.filter((s) => !!capture.shots[s.id]).length
}

// The plan for the current state. The VIN step is there while the shop
// wants the vehicle and we don't have a real VIN (a half-typed one isn't),
// and stays once its photo is taken (that photo is where the VIN came from).
export function planFor(
  capture: CaptureState,
  vin: string,
  shopWantsVehicle: boolean,
): PlannedShot[] {
  return buildShotPlan({
    zones: capture.zones,
    unsure: capture.unsure,
    includeSticker: shopWantsVehicle && (!isVinValid(vin.trim().toUpperCase()) || !!capture.shots.sticker),
  })
}
